/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo, useState } from "react";
import ReactJson from "react-json-view";

import { BEM } from "@adapptio/lib-components";

import { ViewEditorView } from "../ViewEditorView";
import { Panel } from "../../../../ui/components/Panel";
import { Tabs, TABS_STYLE } from "../../../../ui/components/Tabs";
import { TABBED_CONTAINER_TYPE, TabbedContainer } from "../../../../ui/components/TabbedContainer";
import { CodeEditor } from "@adapptio/lib-editor-ui";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { ErrorList } from "../../../../ui/components/ErrorList/ErrorList";
import { BlueprintEditor } from "../../../../ui/components/BlueprintEditor/BlueprintEditor";

/**
 * Component props
 */
export interface IViewEditorDebugProps {
	view: ViewEditorView;
}

const tabs = [
	{
		id: "blueprint",
		// @todo Translate
		label: "Blueprint",
		icon: "mdi/script-text"
	},
	{
		id: "compiledCode",
		// @todo Translate
		label: "Compiled State",
		icon: "mdi/language-javascript"
	},
	{
		id: "compileErrors",
		// @todo Translate
		label: "Compile Errors",
		icon: "mdi/alert"
	},
	{
		id: "runtimeSpec",
		// @todo Translate
		label: "Runtime State",
		icon: "mdi/code-braces"
	}
];

/**
 * Main Application Component
 *
 * @param props Component props
 */
export const ViewEditorDebug: React.FunctionComponent<IViewEditorDebugProps> = ({ view }) => {
	const model = view.getViewModel();

	const [ activeTab, setActiveTab ] = useState("blueprint");
	const [ yamlContents, setYamlContents ] = useState(model.exportDocumentAsYAML());
	const [ compiledContents, setCompiledContents ] = useState(model.compile());
	const [ viewSpec, setViewSpec ] = useState(model.getLastSpec());

	const compileErrors = useMemo(() => {
		return {
			documentUri: null,
			parseErrors: [],
			runtimeErrors: [],
			compileErrors: compiledContents.errors
		};
	}, [ compiledContents ]);

	const handleApplyBlueprintChanges = (value: string) => {
		model.loadDocument(value);
	}

	useSimpleEvent(
		model.onModelChange,
		() => {
			setYamlContents(model.exportDocumentAsYAML());
			setCompiledContents(model.compile());
		},
		[ model ],
		true
	);

	useSimpleEvent(
		model.onRender,
		() => {
			setViewSpec(model.getLastSpec());
		},
		[ model ],
		true
	);

	const baseClass = BEM("edt-module-view-editor-debug");

	return (
		<Panel scroll={false} className={baseClass.toClassName()}>
			<Tabs
				className={baseClass.element("tabs").toClassName()}
				items={tabs}
				selectedId={activeTab}
				onSelect={setActiveTab}
				style={TABS_STYLE.DEFAULT}
			/>
			<TabbedContainer
				selectedId={activeTab}
				style={TABBED_CONTAINER_TYPE.SWITCH}
				className={baseClass.element("content").toClassName()}
				items={[
					{
						id: "blueprint",
						renderWhenHidden: true,
						render: () => <BlueprintEditor initialValue={yamlContents} onApplyChanges={handleApplyBlueprintChanges} />
					},
					{
						id: "compiledCode",
						renderWhenHidden: true,
						render: () => (
							<CodeEditor
								language="javascript"
								readOnly={true}
								value={compiledContents?.code || ""}
							/>
						)
					},
					{
						id: "compileErrors",
						renderWhenHidden: true,
						render: () => <ErrorList errorGroups={[ compileErrors ]} />
					},
					{
						id: "runtimeSpec",
						renderWhenHidden: false,
						render: () => (
							<ReactJson
								src={viewSpec}
								theme="ocean"
								enableClipboard={false}
								collapsed={true}
								displayDataTypes={false}
							/>
						)
					}
				]}
			/>
		</Panel>
	);
};
